import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Permission, toPersianDigits } from '@bookora/shared';
import {
  Alert,
  Button,
  Icon,
  IconButton,
  Input,
  JalaliDate,
  JalaliRangePicker,
  QueryError,
  Space,
  Table,
  Typography,
  toApiRange,
  useConfirm,
  type ColumnsType,
} from '@bookora/ui';
import type { Dayjs } from 'dayjs';

import { FormField } from '@/components/form-field';
import { calendarDayRoute } from '@/features/calendar/links';
import { TableSkeleton } from '@/components/form-bits';
import { useCan } from '@/permissions/use-can';

import { scheduleApi, type TimeOffRow } from './api';
import { useSectionMutation, useTimeOff } from './hooks';

const { Text } = Typography;

/** «ثبت مرخصی» before a range is picked. */
const TIME_OFF_NO_RANGE = 'هنوز بازه‌ای انتخاب نشده. روز شروع و پایان مرخصی رو انتخاب کنید.';

/**
 * Time off for one colleague.
 *
 * The booking engine subtracts these ranges from availability, which is the
 * whole point: «مرخصی» here is not an HR record, it is the reason a patient
 * cannot be given a slot on Thursday. All-day is the default because that is
 * what a clinic actually books around; the API widens an all-day range to the
 * end of the closing day in Tehran time, so nobody has to think about 23:59.
 *
 * Removal is a real delete on the server — a `TimeOff` row carries no clinical
 * history and a cancelled holiday that stays on the calendar keeps blocking
 * bookings. That is the one exception in this section, and it is the API's
 * shape, not a choice made here.
 */
export interface TimeOffEditorProps {
  staffId: string;
  staffName?: string | null;
}

export function TimeOffEditor({ staffId, staffName }: TimeOffEditorProps) {
  const canEdit = useCan(Permission.APPOINTMENT_EDIT);
  const confirm = useConfirm();
  const query = useTimeOff(staffId);

  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [reason, setReason] = useState('');
  const [displaced, setDisplaced] = useState<DisplacedAppointments | null>(null);

  const create = useSectionMutation(
    async (payload: { startAt: string; endAt: string; reason?: string }) => {
      const created = await scheduleApi.createTimeOff({
        staffId,
        startAt: payload.startAt,
        endAt: payload.endAt,
        isAllDay: true,
        reason: payload.reason,
      });
      setDisplaced(readDisplaced(created));
      return created;
    },
    {
      successMessage: 'مرخصی ثبت شد.',
      onDone: () => {
        setRange(null);
        setReason('');
      },
    },
  );

  const remove = useSectionMutation((id: string) => scheduleApi.removeTimeOff(id), {
    successMessage: 'مرخصی حذف شد.',
  });

  if (query.isLoading) return <TableSkeleton rows={3} label="در حال بارگذاری مرخصی‌ها…" />;
  if (query.error) return <QueryError error={query.error} onRetry={() => void query.refetch()} />;

  const columns: ColumnsType<TimeOffRow> = [
    {
      title: 'از',
      dataIndex: 'startAt',
      render: (value: string) => <JalaliDate value={value} withRelative={false} />,
    },
    {
      title: 'تا',
      dataIndex: 'endAt',
      render: (value: string) => <JalaliDate value={value} withRelative={false} />,
    },
    {
      title: 'دلیل',
      dataIndex: 'reason',
      render: (value: string | null | undefined) => value ?? <Text type="secondary">—</Text>,
    },
    ...(canEdit
      ? [
          {
            title: '',
            key: 'actions',
            width: 60,
            render: (_: unknown, row: TimeOffRow) => (
              <IconButton
                type="text"
                danger
                name="trash"
                label={`حذف مرخصی ${row.reason ?? ''}`.trim()}
                onClick={() => {
                  void (async () => {
                    const ok = await confirm({
                      title: 'حذف این مرخصی؟',
                      content: 'با حذف اون، این بازه دوباره برای نوبت‌دهی باز می‌شه.',
                      okText: 'حذف بشه',
                      danger: true,
                    });
                    if (ok) remove.mutate(row.id);
                  })();
                }}
              />
            ),
          },
        ]
      : []),
  ];

  const apiRange = toApiRange(range);

  return (
    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
      {!canEdit ? (
        <Alert
          type="info"
          showIcon
          title="برای ثبت مرخصی، دسترسی «ویرایش و جابه‌جایی نوبت» لازم است."
        />
      ) : null}

      {displaced ? (
        <Alert
          className="bk-enter-fade"
          type="warning"
          showIcon
          closable={{ onClose: () => setDisplaced(null) }}
          title={
            <>
              {toPersianDigits(displaced.count)} نوبت داخل این بازه ثبت شده بود و باید جابه‌جا بشه.
            </>
          }
          description={
            <Space orientation="vertical" size={4}>
              <span>
                مرخصی ثبت شد، ولی زمان این نوبت‌ها تغییر نکرده و هنوز به بیماران خبر داده نشده. از
                تقویم، نوبت‌ها رو جابه‌جا یا لغو کنید و به بیماران اطلاع بدید.
              </span>
              <Link to={calendarDayRoute(displaced.startAt)}>رفتن به تقویم اون روز</Link>
            </Space>
          }
        />
      ) : null}

      <Table<TimeOffRow>
        rowKey="id"
        pagination={false}
        dataSource={query.data ?? []}
        columns={columns}
        scroll={{ x: 480 }}
        locale={{
          emptyText: staffName ? `برای ${staffName} مرخصی‌ای ثبت نشده.` : 'مرخصی‌ای ثبت نشده.',
        }}
      />

      {canEdit ? (
        // Real labels: the range and the reason were a grey caption above an
        // unnamed field, so a screen reader met «ویرایش متن» with no subject.
        <Space wrap align="end">
          <FormField label="بازهٔ مرخصی">
            <JalaliRangePicker value={range} onChange={(value) => setRange(value)} />
          </FormField>
          <div style={{ minInlineSize: 'min(220px, 100%)' }}>
            <FormField label="دلیل (اختیاری)">
              <Input
                value={reason}
                maxLength={120}
                onChange={(event) => setReason(event.target.value)}
                placeholder="مثلاً: سفر"
              />
            </FormField>
          </div>
          <Button
            type="primary"
            icon={<Icon name="add" />}
            loading={create.isPending}
            disabledReason={!apiRange.from || !apiRange.to ? TIME_OFF_NO_RANGE : null}
            onClick={() =>
              create.mutate({
                startAt: apiRange.from as string,
                endAt: apiRange.to as string,
                reason: reason.trim() === '' ? undefined : reason.trim(),
              })
            }
          >
            ثبت مرخصی
          </Button>
        </Space>
      ) : null}
    </Space>
  );
}

interface DisplacedAppointments {
  count: number;
  ids: string[];
  /** The leave's first instant — the calendar day the desk has to clear. */
  startAt: string;
}

/**
 * `POST /time-off` now answers with the live appointments that were already
 * inside the range: `affectedAppointments` (count) and
 * `affectedAppointmentIds` (start-time order). The leave is recorded
 * regardless — refusing it would make «دکتر مریض شد» impossible to enter until
 * every patient had been phoned — so the panel's job is to say who has to be
 * moved, not to block.
 */
function readDisplaced(created: TimeOffRow): DisplacedAppointments | null {
  const ids = created.affectedAppointmentIds ?? [];
  const count = created.affectedAppointments ?? ids.length;
  if (count <= 0) return null;
  return { count, ids, startAt: String(created.startAt) };
}
